export class AnalyticsEngine {
    trades = [];
    curve = [];
    recordClosedTrade(trade) {
        this.trades.push({ ...trade });
    }
    recordEquityPoint(timestamp, equity) {
        this.curve.push({ timestamp, equity });
    }
    getClosedTrades() {
        return this.trades.map((t) => ({ ...t }));
    }
    getEquityCurve() {
        return this.curve.map((p) => ({ ...p }));
    }
    restoreClosedTrades(trades) {
        this.trades = trades.map((t) => ({ ...t }));
    }
    restoreEquityCurve(curve) {
        this.curve = curve.map((p) => ({ ...p }));
    }
    clear() {
        this.trades = [];
        this.curve = [];
    }
    getAnalytics() {
        let grossProfit = 0;
        let grossLoss = 0;
        let netProfit = 0;
        let winningTrades = 0;
        for (const trade of this.trades) {
            // S5: `grossProfit`/`grossLoss` stay on the GROSS `realizedPnl` because that
            // is what the names promise. `netProfit` is now genuinely net - it subtracts
            // this trade's own commission and tax, which after S5 include the entry leg.
            // Before S5 it was `grossProfit - grossLoss`, i.e. a gross number wearing the
            // name `netProfit`.
            const net = trade.realizedPnl - trade.commission - trade.tax;
            netProfit += net;
            if (trade.realizedPnl >= 0) {
                grossProfit += trade.realizedPnl;
                winningTrades += 1;
            }
            else {
                grossLoss += -trade.realizedPnl;
            }
        }
        const totalTrades = this.trades.length;
        const losingTrades = totalTrades - winningTrades;
        let peak = Number.NEGATIVE_INFINITY;
        let maxDrawdown = 0;
        let maxDrawdownPercent = 0;
        for (const point of this.curve) {
            if (point.equity > peak)
                peak = point.equity;
            if (peak > 0) {
                const dd = peak - point.equity;
                if (dd > maxDrawdown) {
                    maxDrawdown = dd;
                    maxDrawdownPercent = (dd / peak) * 100;
                }
            }
        }
        return {
            totalTrades,
            winningTrades,
            losingTrades,
            winRate: totalTrades === 0 ? 0 : winningTrades / totalTrades,
            grossProfit,
            grossLoss,
            netProfit,
            profitFactor: totalTrades === 0 ? 0 : grossLoss === 0 ? grossProfit : grossProfit / grossLoss,
            maxDrawdown,
            maxDrawdownPercent,
            equityCurve: this.getEquityCurve(),
            closedTrades: this.getClosedTrades(),
            sharpeRatio: computeSharpeRatio(this.curve),
        };
    }
}
/**
 * TVP-A4: port EXACT công thức legacy (ReplaySessionAnalytics):
 * sample variance (n-1), skip prev === 0, annualized √252.
 * Giữ `!== 0` như legacy (parity) thay vì E ≤ 0 nghiêm ngặt — chia cho số âm
 * hữu hạn không sinh NaN/Infinity nên an toàn; mọi đường NaN/Infinity
 * (length<3, stdDev 0) đều đã khóa về 0.
 */
export function computeSharpeRatio(curve, periodsPerYear = 252) {
    if (curve.length < 3)
        return 0;
    const returns = [];
    for (let i = 1; i < curve.length; i++) {
        const prev = curve[i - 1].equity;
        const cur = curve[i].equity;
        if (prev !== 0) {
            returns.push((cur - prev) / prev);
        }
    }
    if (returns.length < 2)
        return 0;
    const mean = returns.reduce((s, r) => s + r, 0) / returns.length;
    const variance = returns.reduce((s, r) => s + (r - mean) ** 2, 0) / (returns.length - 1);
    const stdDev = Math.sqrt(variance);
    return stdDev > 0 ? (mean / stdDev) * Math.sqrt(periodsPerYear) : 0;
}
